import { useEffect, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useApi } from '../lib/api';
import { useAuth } from '../contexts/useAuth';
import { cn, hoursToDays } from '../lib/utils';
import { projectStatusBadgeClass, projectStatusLabel } from '../lib/project-status';
import {
    Search,
    Filter,
    FolderOpen,
    Clock,
    Users,
    ChevronRight,
    Loader2,
    Building2,
    Plus,
    X,
    Link2Off,
    Lock,
    Check,
} from 'lucide-react';
import type { ApiResponse, Client, Project } from '../types';

/**
 * Faut-il afficher la pastille « Interne » ?
 *
 * Avant que la notion existe, un projet interne se signalait en tapant « Interne » à la
 * place de son code. Sur ces projets la pastille ferait doublon avec le code affiché juste
 * à côté. Le garde-fou pourra disparaître le jour où ces codes seront renommés.
 */
function showInternalPill(project: Project): boolean {
    return Boolean(project.internal) && project.code.trim().toLowerCase() !== 'interne';
}

export function Projects() {
    const api = useApi();
    const { isAdmin } = useAuth();
    const [isCreating, setIsCreating] = useState(false);
    const [search, setSearch] = useState('');
    const [showOnlyMine, setShowOnlyMine] = useState(true);
    const [showInactive, setShowInactive] = useState(false);
    const [showOnlyUnlinked, setShowOnlyUnlinked] = useState(false);
    const [showClosed, setShowClosed] = useState(false);

    // Un projet supprimé emporte sa fiche : c'est ici qu'on confirme le geste, la page qui
    // l'a déclenché n'existant plus. L'état de navigation est consommé une fois, sinon le
    // message reviendrait à chaque retour sur la liste.
    const location = useLocation();
    const navigate = useNavigate();
    const deletedProject = (location.state as { deletedProject?: { code: string; comesBack: boolean } } | null)
        ?.deletedProject;
    useEffect(() => {
        if (deletedProject) navigate('.', { replace: true, state: null });
    }, [deletedProject, navigate]);

    const { data: projectsData, isLoading } = useQuery({
        queryKey: ['projects', {
            myProjects: showOnlyMine,
            active: !showInactive,
            linked: showOnlyUnlinked ? false : undefined,
            includeClosed: showClosed,
        }],
        queryFn: () =>
            api.get<ApiResponse<Project[]>>('/api/projects', {
                myProjects: showOnlyMine,
                active: !showInactive ? true : undefined,
                // Projets sans commande rattachée : ceux qu'il reste éventuellement à
                // relier au CRM.
                linked: showOnlyUnlinked ? false : undefined,
                // « Mes projets » écarte les projets clôturés, la saisie y étant fermée.
                // Cet indicateur les ramène, pour retrouver ce qui a été clôturé.
                includeClosed: showClosed ? true : undefined,
            }),
    });

    const projects = projectsData?.data || [];

    // Filter projects by search
    const filteredProjects = projects.filter((project) => {
        const searchLower = search.toLowerCase();
        return (
            project.name.toLowerCase().includes(searchLower) ||
            project.code.toLowerCase().includes(searchLower) ||
            project.orderNumber?.toLowerCase().includes(searchLower) ||
            project.clientName?.toLowerCase().includes(searchLower)
        );
    });

    // Group projects by client
    const projectsByClient = filteredProjects.reduce((acc, project) => {
        const clientName = project.clientName || 'Sans client';
        if (!acc[clientName]) {
            acc[clientName] = [];
        }
        acc[clientName].push(project);
        return acc;
    }, {} as Record<string, Project[]>);

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <div>
                    <h1 className="text-2xl font-bold text-[var(--color-text)]">Projets</h1>
                    <p className="text-[var(--color-text-secondary)]">
                        {filteredProjects.length} projet{filteredProjects.length > 1 ? 's' : ''}
                    </p>
                </div>

                {isAdmin && (
                    <button onClick={() => setIsCreating(true)} className="btn-primary">
                        <Plus className="w-4 h-4" />
                        Nouveau projet
                    </button>
                )}
            </div>

            {deletedProject && (
                <div className="card flex items-start gap-3 border-[var(--color-success)]">
                    <Check className="w-5 h-5 text-[var(--color-success)] flex-shrink-0 mt-0.5" />
                    <div className="text-sm">
                        <p className="font-medium text-[var(--color-text)]">
                            Projet « {deletedProject.code} » supprimé
                        </p>
                        {deletedProject.comesBack && (
                            <p className="text-[var(--color-text-secondary)] mt-1">
                                Il venait d'une source externe : si sa ligne SharePoint ou sa commande
                                Dynamics existe toujours, il sera recréé dans les minutes qui suivent.
                            </p>
                        )}
                    </div>
                </div>
            )}

            {isCreating && <NewProjectModal onClose={() => setIsCreating(false)} />}

            {/* Search and filters */}
            <div className="flex flex-col sm:flex-row gap-3">
                <div className="relative flex-1">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[var(--color-text-muted)]" />
                    <input
                        type="text"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        placeholder="Rechercher un projet, un code, un numéro de commande ou un client..."
                        className="input pl-10"
                    />
                </div>

                <div className="flex items-center gap-2">
                    <button
                        onClick={() => setShowOnlyMine(!showOnlyMine)}
                        className={cn(
                            'flex items-center gap-2 px-3 py-2 rounded-lg border transition-colors text-sm',
                            showOnlyMine
                                ? 'bg-[var(--color-primary-light)] border-[var(--color-primary)] text-[var(--color-primary)]'
                                : 'border-[var(--color-border)] text-[var(--color-text-secondary)] hover:bg-[var(--color-bg)]'
                        )}
                    >
                        <Users className="w-4 h-4" />
                        Mes projets
                    </button>

                    <button
                        onClick={() => setShowInactive(!showInactive)}
                        className={cn(
                            'flex items-center gap-2 px-3 py-2 rounded-lg border transition-colors text-sm',
                            showInactive
                                ? 'bg-[var(--color-bg-hover)] border-[var(--color-border)] text-[var(--color-text)]'
                                : 'border-[var(--color-border)] text-[var(--color-text-secondary)] hover:bg-[var(--color-bg)]'
                        )}
                    >
                        <Filter className="w-4 h-4" />
                        Inclure inactifs
                    </button>

                    <button
                        onClick={() => setShowClosed(!showClosed)}
                        className={cn(
                            'flex items-center gap-2 px-3 py-2 rounded-lg border transition-colors text-sm',
                            showClosed
                                ? 'bg-[var(--color-bg-hover)] border-[var(--color-border)] text-[var(--color-text)]'
                                : 'border-[var(--color-border)] text-[var(--color-text-secondary)] hover:bg-[var(--color-bg)]'
                        )}
                        title="Projets clôturés ou terminés, sur lesquels la saisie est fermée"
                    >
                        <Lock className="w-4 h-4" />
                        Inclure clôturés
                    </button>

                    {isAdmin && (
                        <button
                            onClick={() => setShowOnlyUnlinked(!showOnlyUnlinked)}
                            className={cn(
                                'flex items-center gap-2 px-3 py-2 rounded-lg border transition-colors text-sm',
                                showOnlyUnlinked
                                    ? 'bg-[var(--color-warning)] border-[var(--color-warning)] text-white'
                                    : 'border-[var(--color-border)] text-[var(--color-text-secondary)] hover:bg-[var(--color-bg)]'
                            )}
                            title="Projets sans commande rattachée dans le CRM"
                        >
                            <Link2Off className="w-4 h-4" />
                            Sans commande
                        </button>
                    )}
                </div>
            </div>

            {/* Projects list */}
            {isLoading ? (
                <div className="flex items-center justify-center py-12">
                    <Loader2 className="w-8 h-8 animate-spin text-[var(--color-primary)]" />
                </div>
            ) : filteredProjects.length === 0 ? (
                <div className="text-center py-12 card">
                    <FolderOpen className="w-12 h-12 text-[var(--color-text-muted)] mx-auto mb-4" />
                    <p className="text-[var(--color-text-secondary)]">Aucun projet trouvé</p>
                    {search && (
                        <button
                            onClick={() => setSearch('')}
                            className="mt-2 text-[var(--color-primary)] hover:underline"
                        >
                            Effacer la recherche
                        </button>
                    )}
                </div>
            ) : (
                <div className="space-y-6">
                    {Object.entries(projectsByClient)
                        .sort(([a], [b]) => a.localeCompare(b))
                        .map(([clientName, clientProjects]) => (
                            <div key={clientName}>
                                <div className="flex items-center gap-2 mb-3">
                                    <Building2 className="w-4 h-4 text-[var(--color-text-muted)]" />
                                    <h2 className="text-sm font-semibold text-[var(--color-text-secondary)] uppercase tracking-wide">
                                        {clientName}
                                    </h2>
                                    <span className="text-xs text-[var(--color-text-muted)]">
                                        ({clientProjects.length})
                                    </span>
                                </div>
                                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                                    {clientProjects.map((project) => (
                                        <ProjectCard key={project.id} project={project} />
                                    ))}
                                </div>
                            </div>
                        ))}
                </div>
            )}
        </div>
    );
}

interface ProjectCardProps {
    project: Project;
}

function ProjectCard({ project }: ProjectCardProps) {
    // Heures validées (approved/billed)
    const approvedHours = project.consumedHours || 0;
    const approvedPercent = project.budgetHours
        ? Math.min(approvedHours / project.budgetHours * 100, 100)
        : 0;

    // Heures en attente (draft/submitted)
    const pendingHours = project.pendingHours || 0;
    const totalWithPending = approvedHours + pendingHours;
    const pendingPercent = project.budgetHours
        ? Math.min(totalWithPending / project.budgetHours * 100, 100)
        : 0;

    const getStatus = (percent: number) =>
        percent >= 90 ? 'danger' : percent >= 75 ? 'warning' : 'normal';

    const approvedStatus = getStatus(approvedPercent);
    const pendingStatus = getStatus(pendingPercent);

    return (
        <Link
            to={`/projects/${project.id}`}
            className={cn(
                'card block transition-all hover:shadow-md hover:border-[var(--color-primary)]',
                (!project.active || project.closed) && 'opacity-60'
            )}
        >
            <div className="flex items-start justify-between gap-2">
                <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                        <span className="px-2 py-0.5 bg-[var(--color-bg)] text-[var(--color-text-secondary)] text-xs font-medium rounded">
                            {project.code}
                        </span>
                        {project.orderNumber && project.orderNumber !== project.code && (
                            <span
                                className="px-2 py-0.5 bg-[var(--color-bg)] text-[var(--color-text-muted)] text-xs rounded"
                                title="Numéro de commande"
                            >
                                {project.orderNumber}
                            </span>
                        )}
                        {/* Projet interne : au gabarit de la pastille Dynamics, celle qui qualifie la
                            nature d'un projet, et non à celui des pastilles de statut. La règle
                            vient de l'API, jamais du nom du client. */}
                        {showInternalPill(project) && (
                            <span
                                className="px-2 py-0.5 bg-[var(--color-primary-light)] text-[var(--color-primary)] text-xs rounded"
                                title="Projet interne à l'entreprise"
                            >
                                Interne
                            </span>
                        )}
                        {!project.active && (
                            <span className="px-2 py-0.5 bg-[var(--color-bg-hover)] text-[var(--color-text-muted)] text-xs rounded">
                                Inactif
                            </span>
                        )}
                        <span className={projectStatusBadgeClass(project.status)}>
                            {project.closed && <Lock className="w-3 h-3" />}
                            {projectStatusLabel(project.status)}
                        </span>
                    </div>
                    <h3 className="font-medium text-[var(--color-text)] truncate">{project.name}</h3>
                </div>
                <ChevronRight className="w-5 h-5 text-[var(--color-text-muted)] flex-shrink-0" />
            </div>

            {project.description && (
                <p className="text-sm text-[var(--color-text-secondary)] mt-2 line-clamp-2">
                    {project.description}
                </p>
            )}

            {/* Budget progress - Validés */}
            {project.budgetHours && (
                <div className="mt-4 space-y-2">
                    {/* Heures validées (approved/billed) */}
                    <div>
                        <div className="flex items-center justify-between text-sm mb-1">
                            <span className="text-[var(--color-text-secondary)] flex items-center gap-1">
                                <Clock className="w-3 h-3" />
                                Validé
                            </span>
                            <span
                                className={cn(
                                    'font-medium',
                                    approvedStatus === 'danger' && 'text-[var(--color-error)]',
                                    approvedStatus === 'warning' && 'text-[var(--color-warning)]',
                                    approvedStatus === 'normal' && 'text-[var(--color-text-secondary)]'
                                )}
                            >
                                {hoursToDays(approvedHours)}j / {hoursToDays(project.budgetHours)}j
                            </span>
                        </div>
                        <div className="h-2 bg-[var(--color-bg)] rounded-full overflow-hidden">
                            <div
                                className={cn(
                                    'h-full rounded-full transition-all',
                                    approvedStatus === 'danger' && 'bg-[var(--color-error)]',
                                    approvedStatus === 'warning' && 'bg-[var(--color-warning)]',
                                    approvedStatus === 'normal' && 'bg-[var(--color-primary)]'
                                )}
                                style={{ width: `${approvedPercent}%` }}
                            />
                        </div>
                    </div>

                    {/* Heures en attente (draft/submitted) */}
                    {pendingHours > 0 && (
                        <div>
                            <div className="flex items-center justify-between text-sm mb-1">
                                <span className="text-[var(--color-text-muted)] flex items-center gap-1 text-xs">
                                    <Clock className="w-3 h-3" />
                                    + En attente
                                </span>
                                <span
                                    className={cn(
                                        'font-medium text-xs',
                                        pendingStatus === 'danger' && 'text-[var(--color-error)]',
                                        pendingStatus === 'warning' && 'text-[var(--color-warning)]',
                                        pendingStatus === 'normal' && 'text-[var(--color-text-muted)]'
                                    )}
                                >
                                    {hoursToDays(totalWithPending)}j / {hoursToDays(project.budgetHours)}j
                                    <span className="text-[var(--color-text-muted)] ml-1">
                                        (+{hoursToDays(pendingHours)}j)
                                    </span>
                                </span>
                            </div>
                            <div className="h-1.5 bg-[var(--color-bg)] rounded-full overflow-hidden">
                                <div
                                    className={cn(
                                        'h-full rounded-full transition-all opacity-60',
                                        pendingStatus === 'danger' && 'bg-[var(--color-error)]',
                                        pendingStatus === 'warning' && 'bg-[var(--color-warning)]',
                                        pendingStatus === 'normal' && 'bg-[var(--color-primary)]'
                                    )}
                                    style={{ width: `${pendingPercent}%` }}
                                />
                            </div>
                        </div>
                    )}
                </div>
            )}

            {/* Dates */}
            {(project.startDate || project.endDate) && (
                <div className="mt-3 pt-3 border-t border-[var(--color-border)]">
                    <p className="text-xs text-[var(--color-text-muted)]">
                        {project.startDate && (
                            <>Du {new Date(project.startDate).toLocaleDateString('fr-FR')}</>
                        )}
                        {project.startDate && project.endDate && ' '}
                        {project.endDate && (
                            <>au {new Date(project.endDate).toLocaleDateString('fr-FR')}</>
                        )}
                    </p>
                </div>
            )}
        </Link>
    );
}

/**
 * Création d'un projet à la main, sans passer par une synchronisation.
 *
 * Deux usages : un chantier interne, qui n'a pas de commande, et un projet dont la
 * commande n'est pas encore saisie dans le CRM. Dans ce second cas, renseigner le numéro
 * de commande dès maintenant suffit : la synchronisation Dynamics reconnaîtra ce projet
 * le jour où la commande existera, et l'adoptera au lieu d'en créer un second.
 */
function NewProjectModal({ onClose }: { onClose: () => void }) {
    const api = useApi();
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const [error, setError] = useState<string | null>(null);
    const [form, setForm] = useState({
        name: '',
        code: '',
        orderNumber: '',
        clientId: '',
        soldDays: '',
        startDate: '',
        endDate: '',
        description: '',
        isBillable: true,
    });

    const { data: clientsData } = useQuery({
        queryKey: ['clients', { active: true }],
        queryFn: () => api.get<ApiResponse<Client[]>>('/api/clients', { active: true }),
    });

    const createMutation = useMutation({
        mutationFn: () =>
            api.post<ApiResponse<Project>>('/api/projects', {
                name: form.name.trim(),
                code: form.code.trim() || undefined,
                orderNumber: form.orderNumber.trim() || undefined,
                clientId: form.clientId || undefined,
                soldDays: form.soldDays.trim() ? Number(form.soldDays.replace(',', '.')) : undefined,
                startDate: form.startDate || undefined,
                endDate: form.endDate || undefined,
                description: form.description.trim() || undefined,
                isBillable: form.isBillable,
            }),
        onSuccess: (res) => {
            queryClient.invalidateQueries({ queryKey: ['projects'] });
            onClose();
            // Le filtre « Mes projets », actif par défaut, cachait le projet qu'on venait
            // de créer, faute d'y être affecté : on ouvre sa fiche, où l'affecter.
            if (res.data?.id) navigate(`/projects/${res.data.id}`);
        },
        onError: (err: Error) => setError(err.message || 'Création impossible'),
    });

    const set = <K extends keyof typeof form>(key: K, value: (typeof form)[K]) =>
        setForm((prev) => ({ ...prev, [key]: value }));

    const canSubmit = form.name.trim() !== '' && (form.code.trim() !== '' || form.orderNumber.trim() !== '');

    return (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="card w-full max-w-lg max-h-[90vh] overflow-y-auto">
                <div className="p-6 border-b border-[var(--color-border)] flex items-start justify-between gap-4">
                    <div>
                        <h2 className="text-lg font-semibold text-[var(--color-text)]">Nouveau projet</h2>
                        <p className="text-sm text-[var(--color-text-secondary)] mt-1">
                            Créé à la main, sans synchronisation. Aucune source externe ne le modifiera,
                            sauf s'il est rattaché à une commande.
                        </p>
                    </div>
                    <button
                        type="button"
                        onClick={onClose}
                        className="p-1 text-[var(--color-text-muted)] hover:bg-[var(--color-bg)] rounded"
                        aria-label="Fermer"
                    >
                        <X className="w-5 h-5" />
                    </button>
                </div>

                <div className="p-6 space-y-4">
                    <div>
                        <label className="block text-sm font-medium text-[var(--color-text-secondary)] mb-1">
                            Nom du projet *
                        </label>
                        <input
                            type="text"
                            value={form.name}
                            onChange={(e) => set('name', e.target.value)}
                            placeholder="CLIENT - Intitulé de la prestation"
                            className="input"
                            autoFocus
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-[var(--color-text-secondary)] mb-1">
                            Client
                        </label>
                        <select
                            value={form.clientId}
                            onChange={(e) => set('clientId', e.target.value)}
                            className="input"
                        >
                            <option value="">Aucun</option>
                            {(clientsData?.data ?? []).map((c) => (
                                <option key={c.id} value={c.id}>{c.name}</option>
                            ))}
                        </select>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-[var(--color-text-secondary)] mb-1">
                                Numéro de commande
                            </label>
                            <input
                                type="text"
                                value={form.orderNumber}
                                onChange={(e) => set('orderNumber', e.target.value)}
                                placeholder="ORD-01234-ABCDEF"
                                className="input"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-[var(--color-text-secondary)] mb-1">
                                Code projet
                            </label>
                            <input
                                type="text"
                                value={form.code}
                                onChange={(e) => set('code', e.target.value)}
                                placeholder="repris du numéro de commande"
                                className="input"
                            />
                        </div>
                    </div>
                    <p className="text-xs text-[var(--color-text-muted)]">
                        L'un des deux suffit : sans code, c'est le numéro de commande qui en tient lieu.
                        Renseigner le numéro permettra à la synchronisation Dynamics de reconnaître ce
                        projet plutôt que d'en créer un second.
                    </p>

                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-[var(--color-text-secondary)] mb-1">
                                Jours vendus
                            </label>
                            <input
                                type="number"
                                min="0"
                                step="0.5"
                                value={form.soldDays}
                                onChange={(e) => set('soldDays', e.target.value)}
                                className="input"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-[var(--color-text-secondary)] mb-1">
                                Début
                            </label>
                            <input
                                type="date"
                                value={form.startDate}
                                onChange={(e) => set('startDate', e.target.value)}
                                className="input"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-[var(--color-text-secondary)] mb-1">
                                Fin
                            </label>
                            <input
                                type="date"
                                value={form.endDate}
                                min={form.startDate || undefined}
                                onChange={(e) => set('endDate', e.target.value)}
                                className="input"
                            />
                        </div>
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-[var(--color-text-secondary)] mb-1">
                            Description
                        </label>
                        <textarea
                            value={form.description}
                            onChange={(e) => set('description', e.target.value)}
                            rows={2}
                            className="input"
                        />
                    </div>

                    <label className="flex items-center gap-2 text-sm text-[var(--color-text-secondary)]">
                        <input
                            type="checkbox"
                            checked={form.isBillable}
                            onChange={(e) => set('isBillable', e.target.checked)}
                        />
                        Projet facturable
                    </label>

                    {error && <p className="text-sm text-[var(--color-error)] break-words">{error}</p>}
                </div>

                <div className="p-6 border-t border-[var(--color-border)] flex justify-end gap-2">
                    <button type="button" onClick={onClose} className="btn-secondary">
                        Annuler
                    </button>
                    <button
                        type="button"
                        onClick={() => createMutation.mutate()}
                        disabled={!canSubmit || createMutation.isPending}
                        className="btn-primary"
                    >
                        {createMutation.isPending && <Loader2 className="w-4 h-4 animate-spin" />}
                        Créer le projet
                    </button>
                </div>
            </div>
        </div>
    );
}
